/**
 * Pure pathname predicates for SideNav active-state detection.
 *
 * Ported from the legacy sidebar's class helpers
 * (`~/dashboard/components/Sidebar/Sidebar.jsx`: `getNavLinkClass`,
 * `getEntityEvaluationLinkClass`, etc.) so both navs highlight identically
 * during the transition.
 */

/**
 * Active when the current pathname starts with the given route path.
 *
 * (Legacy `getNavLinkClass`)
 */
export const isPrefixActive = (path: string, pathname: string): boolean => (
  Boolean(path && pathname && pathname.slice(0, path.length) === path)
);

/**
 * Active-state for the Evaluations and Entities items, which share URL space:
 * an `/entities/:id/evaluations/:id` path activates Evaluations, not Entities.
 *
 * (Legacy `getEntityEvaluationLinkClass`)
 */
export const isEntityEvaluationActive = (path: string, pathname: string): boolean => {
  if (!path || !pathname) return false;

  const evalPath = path.indexOf('/evaluations') > -1;
  const evalPathname = pathname.indexOf('/evaluations') > -1;
  if (evalPath && evalPathname) return true;

  const entityPath = path.indexOf('/entities') > -1;
  const entityPathname = pathname.indexOf('/entities') > -1;
  return entityPath && entityPathname && !evalPathname;
};

/**
 * Workflows is active for both workflow and snippet views.
 *
 * (Legacy `getWorkflowsNavLinkClass`)
 */
export const isWorkflowsActive = (pathname: string): boolean => Boolean(pathname && (pathname.includes('workflows') || pathname.includes('snippets')));

/**
 * Journeys (and Home) are active on a prefix match, except when viewing the
 * applications or alerts queues nested under the journeys URL space.
 *
 * (Legacy `getJourneyNavLinkClass`)
 */
export const isJourneyActive = (path: string, pathname: string): boolean => !pathname.includes('applications')
  && !pathname.includes('alerts')
  && isPrefixActive(path, pathname);

/**
 * (Legacy `getApplicationReviewNavLinkClass`)
 */
export const isApplicationQueueActive = (path: string, pathname: string): boolean => pathname.includes('applications') && isPrefixActive(path, pathname);

/**
 * (Legacy `getAlertReviewNavLinkClass`)
 */
export const isAlertQueueActive = (path: string, pathname: string): boolean => pathname.includes('alerts') && isPrefixActive(path, pathname);

/**
 * (Legacy `getInvestigationsNavLinkClass`)
 */
export const isInvestigationsActive = (path: string, pathname: string): boolean => pathname.includes('investigations') && isPrefixActive(path, pathname);
